import * as Yup from 'yup';
import { DateTime } from 'luxon';

import * as lookups from './lookups';
import { parseDateString } from './utils';

const { trainingOptions, professionalBodies } = lookups;

const eighteenYearsAgo = DateTime.now().minus({ years: 18 }).toJSDate();

export type Schemas = {
    [key: string]: Yup.ObjectSchema;
};

const schemas: Schemas = {
    personalDetails: Yup.object({
        name: Yup.string(),
        email: Yup.string(),
        phoneNumber: Yup.string(),
        dateOfBirth: Yup.date()
            .required('Date of Birth | This field is required.')
            .transform(parseDateString)
            .typeError('Date of Birth | Date must be in the format DD/MM/YYYY.')
            .max(
                eighteenYearsAgo,
                'Date of Birth | You must be at least 18 years old to register.',
            ),
        niNumber: Yup.string()
            .required('National Insurance Number | This field is required.')
            .matches(
                /^[A-Za-z]{2}\d{6}[A-Za-z]{1}$/,
                'National Insurance Number | Your National Insurance number should be in the format of two letters, six digits, one letter.',
            ),
        addressLine1: Yup.string().required('Address Line 1 | This field is required.'),
        addressLine2: Yup.string(),
        town: Yup.string().required('Town | This field is required.'),
        county: Yup.string(),
        postCode: Yup.string().required('Post Code | This field is required.'),
        emergencyContact: Yup.string().required('Emergency Contact | This field is required.'),
        deviceType: Yup.string().required(
            'Device which will have the Doc Abode app installed? | This field is required.',
        ),
    }),
    financialDetails: Yup.object({
        nameOnAccount: Yup.string().required('Name on Account | This field is required.'),
        accountNumber: Yup.string()
            .required('Account Number | this field is required.')
            .matches(
                /^\d{8}$/,
                'Account Number | Your account number should be eight digits only.',
            ),
        sortCode: Yup.string()
            .required('Sort Code | This field is required.')
            .matches(
                /^\d{2}-?\d{2}-?\d{2}$/,
                'Sort Code | Your sort code should be six digits, optionally separated by dashes.',
            ),
        tradingStatus: Yup.string().required(
            'How are you engaging with Doc Abode or the healthcare provider? | This field is required.',
        ),
        inNHSPension: Yup.string().required(
            'Are you currently a member of the NHS pension scheme? | This field is required.',
        ),
        pensionSDNumber: Yup.string().when(
            'inNHSPension',
            (inNHSPension: string, schema: Yup.StringSchema) => {
                return inNHSPension === 'yes'
                    ? schema
                          .required('Pension SD number | This field is required.')
                          .matches(
                              /^\d{8}$/,
                              'Pension SD number | Your pension SD number should be eight numerical digits.',
                          )
                    : schema;
            },
        ),
        pensionTierDeduction: Yup.string().when(
            'inNHSPension',
            (inNHSPension: string, schema: Yup.StringSchema) => {
                return inNHSPension === 'yes'
                    ? schema.required('Pension scheme tier % deduction | This field is required.')
                    : schema;
            },
        ),
        companyName: Yup.string().when(
            'tradingStatus',
            (tradingStatus: string, schema: Yup.StringSchema) => {
                return tradingStatus === 'COMPANY'
                    ? schema.required('Company Name | This field is required.')
                    : schema;
            },
        ),
        companyRegistrationNumber: Yup.string().when(
            'tradingStatus',
            (tradingStatus: string, schema: Yup.StringSchema) => {
                return tradingStatus === 'COMPANY'
                    ? schema
                          .required('Company Registration Number | This field is required.')
                          .matches(
                              /^.{8}$/,
                              'Company Registration Number | Your company number should be eight characters long.',
                          )
                    : schema;
            },
        ),
        vatRegistered: Yup.string().when(
            'tradingStatus',
            (tradingStatus: string, schema: Yup.StringSchema) => {
                return tradingStatus === 'COMPANY'
                    ? schema.required('Are you VAT registered? | This field is required.')
                    : schema;
            },
        ),
        vatRegistrationNumber: Yup.string().when(
            ['tradingStatus', 'vatRegistered'],
            (tradingStatus: string, vatRegistered: string, schema: Yup.StringSchema) => {
                return tradingStatus === 'COMPANY' && vatRegistered === 'yes'
                    ? schema
                          .required('This field is required.')
                          .matches(
                              /^GB\d{7}$/,
                              'VAT Registration Number | Your VAT registration number should be "GB" followed by seven digits.',
                          )
                    : schema;
            },
        ),
    }),
    equalityAndDiversity: Yup.object({
        equalityGender: Yup.string(),
        equalityGenderOther: Yup.string(),
        equalityMaritalStatus: Yup.string(),
        equalityEthnicOrigin: Yup.string(),
        equalityCountryOfBirth: Yup.string(),
        equalityNationality: Yup.string(),
        equalityDisability: Yup.string(),
        equalityReligiousBeliefs: Yup.string(),
        equalitySexualOrientation: Yup.string(),
    }),
    preferences: Yup.object({
        specialities: Yup.array(),
        specialitiesOther: Yup.string(),
        languages: Yup.array(),
        languagesOther: Yup.string(),
    }),
    professionalDetails: Yup.object({
        professionalBodyRegistrationRequired: Yup.string()
            .nullable()
            .test(
                'pbr-required',
                'Does your role require you to be registered with a professional body? | This field is required.',
                function test(value) {
                    return Boolean(
                        this.parent.hcpType === 'GP' ||
                            (this.parent.hcpType !== 'GP' && value && value.length > 0),
                    );
                },
            ),
        professionalBodyType: Yup.string().test(
            'pbt-required',
            'Professional Body | This field is required.',
            function test(value) {
                return Boolean(
                    this.parent.hcpType === 'GP' ||
                        this.parent.professionalBodyRegistrationRequired === 'no' ||
                        (this.parent.professionalBodyRegistrationRequired === 'yes' &&
                            value &&
                            value.length > 0),
                );
            },
        ),
        professionalBodyTypeOther: Yup.string().when(
            'Professional Body (other) | professionalBodyType',
            (professionalBodyType: string, schema: Yup.StringSchema) => {
                return professionalBodyType === 'other'
                    ? schema.required('This field is required.')
                    : schema;
            },
        ),
        professionalBodyId: Yup.string()
            .test('pbid-required', 'Professional Body ID | This field is required.', function test(
                value,
            ) {
                return Boolean(
                    (this.parent.hcpType === 'GP' && value && value.length > 0) ||
                        this.parent.professionalBodyRegistrationRequired === 'no' ||
                        (this.parent.professionalBodyRegistrationRequired === 'yes' &&
                            value &&
                            value.length > 0),
                );
            })
            .test('pbid-format', '', function test(value) {
                const isGP = this.parent.hcpType === 'GP';
                const selectedProfessionalBodyType = isGP
                    ? 'GMC'
                    : this.parent.professionalBodyType;
                const selectedProfessionalBody = selectedProfessionalBodyType
                    ? professionalBodies[selectedProfessionalBodyType]
                    : null;

                if (!selectedProfessionalBody || !value || value.length < 1) {
                    return true;
                }

                const regexToUse = selectedProfessionalBody ? selectedProfessionalBody.regex : null;

                if (!regexToUse) {
                    return true;
                }

                const re = new RegExp(regexToUse);
                const message = `Professional Body ID | ${
                    selectedProfessionalBody.regexHelp || 'The provided ID format is incorrect.'
                }`;
                return re.test(value) || this.createError({ message });
            }),
        primaryGPFunction: Yup.string().when(
            'hcpType',
            (hcpType: string, schema: Yup.StringSchema) => {
                return hcpType === 'GP'
                    ? schema.required('Primary GP Function | This field is required.')
                    : schema;
            },
        ),
        gmcRegistrationDate: Yup.date()
            .transform(parseDateString)
            .when('hcpType', (hcpType: string, schema: Yup.DateSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .typeError(
                              'GMC Registration Date | Date must be in the format DD/MM/YYYY.',
                          )
                          .max(
                              new Date(),
                              'GMC Registration Date | Date entered must be in the past.',
                          )
                          .required('GMC Registration Date | This field is required.')
                    : schema;
            }),
        confirmComplaints: Yup.boolean().when(
            'hcpType',
            (hcpType: string, schema: Yup.BooleanSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .required()
                          .oneOf(
                              [true],
                              'I am not aware of any complaints against me that have been referred to independent review by NHSE or the GMC | This field is required.',
                          )
                    : schema;
            },
        ),
        confirmRestrictions: Yup.boolean().when(
            'hcpType',
            (hcpType: string, schema: Yup.BooleanSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .required()
                          .oneOf(
                              [true],
                              'I have no restrictions on practice or changes in GMC registration status | This field is required.',
                          )
                    : schema;
            },
        ),
        confirmOnPerformersList: Yup.boolean().when(
            'hcpType',
            (hcpType: string, schema: Yup.BooleanSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .required()
                          .oneOf(
                              [true],
                              'I am currently included on the NHS England Performers List | This field is required.',
                          )
                    : schema;
            },
        ),
        confirmNotPerformersListSuspended: Yup.boolean().when(
            'hcpType',
            (hcpType: string, schema: Yup.BooleanSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .required()
                          .oneOf(
                              [true],
                              'I have not been suspended from the NHS England Performers List | This field is required.',
                          )
                    : schema;
            },
        ),
        confirmObligations: Yup.boolean().when(
            'hcpType',
            (hcpType: string, schema: Yup.BooleanSchema) => {
                return hcpType === 'GP'
                    ? schema
                          .required()
                          .oneOf(
                              [true],
                              'I accept the professional obligations placed on me in Good Medical Practice paragraphs 28-30 (GMC 2013) about my personal health | This field is required.',
                          )
                    : schema;
            },
        ),
        confirmAcceptTerms: Yup.boolean()
            .required()
            .oneOf([true], 'I accept the Doc Abode Terms and Conditions | This field is required.'),
        practiceName: Yup.string(),
        practiceODSCode: Yup.string(),
        nhsSmartcardNo: Yup.string().required('NHS Smartcard Number | This field is required.'),
        nhsEmail: Yup.string(),
        medicalIndemnityOrganisation: Yup.string().required(
            'Please state your Medical Indemnity Organisation | This field is required.',
        ),
        files: Yup.array(
            Yup.object({
                category: Yup.string(),
            }),
        ).test('contains-required', '', function test(files) {
            if (!Array.isArray(files)) return false;
            const requiredFiles = ['EMPLOYMENT_HISTORY'];
            const missingFiles = requiredFiles.filter(
                (category) => !files.some((file: any) => file?.category === category),
            );
            if (missingFiles.length > 0) {
                throw this.createError({
                    message: `Employment history and professional qualifications | ${missingFiles.join(
                        ',',
                    )}`,
                });
            }
            return true;
        }),
    }),
    transport: Yup.object({
        hasCurrentDrivingLicence: Yup.string().required(
            'Do you possess a current driving licence? | This field is required.',
        ),
        drivingLicenceNumber: Yup.string().when(
            'hasCurrentDrivingLicence',
            (hasCurrentDrivingLicence: string, schema: Yup.StringSchema) => {
                return hasCurrentDrivingLicence === 'yes'
                    ? schema.required('Driving licence number | This field is required.')
                    : schema;
            },
        ),
        hasCarBusinessInsurance: Yup.string().when(
            'hasCurrentDrivingLicence',
            (hasCurrentDrivingLicence: string, schema: Yup.StringSchema) => {
                return hasCurrentDrivingLicence === 'yes'
                    ? schema.required(
                          'Do you hold valid business insurance for your car? | This field is required.',
                      )
                    : schema;
            },
        ),
        hasConviction: Yup.string().when(
            'hasCurrentDrivingLicence',
            (hasCurrentDrivingLicence: string, schema: Yup.StringSchema) => {
                return hasCurrentDrivingLicence === 'yes'
                    ? schema.required(
                          'Have you ever been convicted of an offence (including motoring but not parking offences) or are any charges or summons at present outstanding against you? | This field is required.',
                      )
                    : schema;
            },
        ),
        convictionDetails: Yup.string().when(
            'hasConviction',
            (hasConviction: string, schema: Yup.StringSchema) => {
                return hasConviction === 'yes'
                    ? schema.required(
                          'Please give details of the conviction | This field is required.',
                      )
                    : schema;
            },
        ),
    }),
    firstReference: Yup.object({
        reference1FullName: Yup.string().required(
            'First reference full name | This field is required.',
        ),
        reference1Address1: Yup.string().required(
            'First reference address line 1 | This field is required.',
        ),
        reference1Address2: Yup.string(),
        reference1Town: Yup.string().required('First reference town | This field is required.'),
        reference1County: Yup.string(),
        reference1PostCode: Yup.string().required(
            'First reference post code | This field is required.',
        ),
        reference1Telephone: Yup.string().required(
            'First reference telephone number | This field is required.',
        ),
        reference1Email: Yup.string()
            .email('First reference email address | Invalid email address provided.')
            .required('First reference email address | This field is required.'),
        reference1EmailConfirm: Yup.string()
            .email('First reference confirm email address | Invalid email address provided.')
            .required('First reference confirm email address | This field is required.')
            .test(
                'emails-match',
                "First reference confirm email address | Email address doesn't match.",
                function test(value) {
                    return this.parent.reference1Email === value;
                },
            ),
    }),
    secondReference: Yup.object({
        reference2FullName: Yup.string().required(
            'Second reference full name | This field is required.',
        ),
        reference2Address1: Yup.string().required(
            'Second reference address line 1 | This field is required.',
        ),
        reference2Address2: Yup.string(),
        reference2Town: Yup.string().required('Second reference town | This field is required.'),
        reference2County: Yup.string(),
        reference2PostCode: Yup.string().required(
            'Second reference post code | This field is required.',
        ),
        reference2Telephone: Yup.string().required(
            'Second reference telephone number | This field is required.',
        ),
        reference2Email: Yup.string()
            .email('Second reference email address | Invalid email address provided.')
            .required('Second reference email address | This field is required.'),
        reference2EmailConfirm: Yup.string()
            .email('Second reference confirm email address | Invalid email address provided.')
            .required('Second reference confirm email address | This field is required.')
            .test(
                'emails-match',
                "Second reference confirm email address | Email address doesn't match.",
                function test(value) {
                    return this.parent.reference2Email === value;
                },
            ),
    }),
    supportingDocumentation: Yup.object({
        files: Yup.array(
            Yup.object({
                category: Yup.string(),
            }),
        ).test('contains-required', '', function test(files) {
            if (!Array.isArray(files)) return false;
            const requiredFiles = ['PROOF_OF_RIGHT_TO_WORK', 'DBS_CERT', 'PROOF_OF_INDEMNITY'];
            const missingFiles = requiredFiles.filter(
                (category) => !files.some((file: any) => file?.category === category),
            );
            if (missingFiles.length > 0) {
                throw this.createError({
                    message: `Supporting documentation | ${missingFiles.join(',')}`,
                });
            }
            return true;
        }),
        vaccinations: Yup.array().test(
            'vaccinations',
            'Please select which routine vaccinations you have received | This field is required.',
            (values) => (values ? values.includes('MMR') && values.includes('Hepatitis B') : false),
        ),
        covidStatus: Yup.string().required('Latest COVID-19 status | This field is required.'),
    }),
    trainingAndDevelopment: Yup.object({
        files: Yup.array(
            Yup.object({
                category: Yup.string(),
            }),
        ).test('contains-required', '', function test(files) {
            if (!Array.isArray(files)) return false;
            const requiredFiles = this.parent.training.map(
                (cert: string) => trainingOptions.find((option) => option.name === cert)?.category,
            );
            const missingFiles = requiredFiles.filter(
                (category: string) => !files.some((file: any) => file?.category === category),
            );
            if (missingFiles.length > 0) {
                throw this.createError({
                    message: `Training and development | ${missingFiles.join(',')}`,
                });
            }
            return true;
        }),
        training: Yup.array(),
        elfhRegistered: Yup.string(),
        systemProficiency: Yup.array().required(),
        systemProficiencyOther: Yup.string().when(
            'systemProficiency',
            (systemProficiency: string[], schema: Yup.StringSchema) => {
                return systemProficiency && systemProficiency.includes('Other')
                    ? schema.required(
                          'Please confirm which systems you are proficient with | This field is required.',
                      )
                    : schema;
            },
        ),
    }),
    declaration: Yup.object({
        confirmCorrect: Yup.boolean()
            .required()
            .oneOf(
                [true],
                'I confirm that the information I have provided is correct and understand that misleading statements may be sufficient grounds for cancelling any agreements made | You must check this box to continue.',
            ),
        confirmUnansweredFollowup: Yup.boolean()
            .required()
            .oneOf(
                [true],
                'I understand that questions left unanswered or partially answered may be discussed following this application | You must check this box to continue.',
            ),
        receiveMarketing: Yup.boolean(),
        confirmBasicAdvancedLifeSupport: Yup.boolean()
            .required()
            .oneOf(
                [true],
                'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Basic/Advanced Life Support | You must check this box to continue.',
            ),
        confirmSafeguardingAdults: Yup.boolean()
            .required()
            .oneOf(
                [true],
                'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Safeguarding Adults | You must check this box to continue.',
            ),
        confirmSafeguardingChildren: Yup.boolean()
            .required()
            .oneOf(
                [true],
                'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Safeguarding Children | You must check this box to continue.',
            ),
    }),
};

export default schemas;
